import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Handles rate limit errors (429) with user-friendly messages
 * Implements exponential backoff for retries
 */
export async function handleRateLimitError(
  error: any,
  retryFn?: () => Promise<any>,
  maxRetries: number = 3
): Promise<any> {
  if (error?.status === 429 || error?.response?.status === 429) {
    const retryAfter =
      error?.response?.headers?.get("Retry-After") || error?.headers?.get("Retry-After") || "60";
    const retrySeconds = parseInt(retryAfter, 10);

    // Show user-friendly error message
    const errorMessage =
      error?.response?.data?.message ||
      error?.data?.message ||
      `Too many requests. Please wait ${retrySeconds} seconds before trying again.`;

    // If retry function provided and retries remaining, wait and retry
    if (retryFn && maxRetries > 0) {
      await new Promise((resolve) => setTimeout(resolve, retrySeconds * 1000));
      return handleRateLimitError(await retryFn(), retryFn, maxRetries - 1);
    }

    throw new Error(errorMessage);
  }

  throw error;
}

/**
 * Wraps a fetch call with rate limit error handling
 */
export async function fetchWithRateLimitHandling(
  url: string,
  options?: RequestInit,
  retries: number = 3
): Promise<Response> {
  try {
    const response = await fetch(url, options);

    if (response.status === 429) {
      const retryAfter = response.headers.get("Retry-After") || "60";
      const retrySeconds = parseInt(retryAfter, 10);

      if (retries > 0) {
        await new Promise((resolve) => setTimeout(resolve, retrySeconds * 1000));
        return fetchWithRateLimitHandling(url, options, retries - 1);
      }

      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `Rate limit exceeded. Please wait ${retrySeconds} seconds.`
      );
    }

    return response;
  } catch (error: any) {
    if (error.status === 429) {
      throw new Error("Rate limit exceeded. Please try again later.");
    }
    throw error;
  }
}
